const utils = require('./utils')

// protect the actionRegistry against outside manipulation within this internal storage.
const internals = {
    actionRegistry: {},             // stores all registered actions by key
    reperformActionRegistry: {},    // stores all registered actions which should be re-performed later by a given group key (e.g., re-login)

    // in case of client side error handling sometimes multiple errors occur as a consequence of one action originating from different componentTree locations and followup actions.
    // to prevent the user from being spammed with error messages a component may decide to skip all further error handling until the original action has been completed.
    // this flag stores either false or the componentIndex of the component which ordered to skip further error handling.
    // @see frontend-controller/lib/actionRegistry.js isFurtherErrorHandlingSkipped()
    // @see frontend-controller/lib/actionRegistry.js skipFurtherErrorHandling()
    // @see frontend-controller/lib/actionRegistry.js releaseFurtherErrorHandling()
    // e.g.: 	login to the cms and open a news entry whose news entry and route does not exist any longer.
    // 			this is possible in a multi-user environment.
    // 			the newsProperties-form will complain about the missing newsEntry and the routeContentTrees about the missing route.
    // 			the corresponding errors will be handled by the newsTable. But at the time the first error occurs no-one can tell if the second error will.
    //			at the moment the newsTable handles the first error it may decide to skip further error handling.
    // 			this way the system works as expected from a user's point of view either if both errors occur or if only one of them does.
    skipFurtherErrorHandlingViaComponentIndex: false, // identifies the component which has ordered to skip further error handling which is necessary to prevent a release via another component.
    skipDeferred: false, // this jquery.Deferred object is used to handle the promise chain necessary for promises to wait until release.

    /**
     * get next free key in the actionRegistry
     *
     * @returns {string}
     */
    getKey: () => {
        const min = utils.collectionSize(internals.actionRegistry);
        const max = (2 * min) + 1;
        for(let i = min; i < max; i++){
            const key = `actionRegistryKey_${i}`
            if(!internals.actionRegistry[key]){
               return key
            }
        }
        console.error('next free actionRegistryKey not found!');
        return `actionRegistryKey_Error`
    }
}

/**
 * actionRegistry management functions.
 */
module.exports = {



    //////////////////////////////////
    ///  main registry
    //////////////////////////////////

    /**
     * registers an action context. usually an action context will contain a component, an action and some additional flags.
     * to identify the given action context will get set a _actionRegistryKey property which must not be manipulated from the outside.
     *
     * @param context           object - action context
     * @returns object - action context referenced within the internal actionRegistryStorage.
     */
    register: function(context){
        if(!context) context = {};

        // debug information
        if(context._actionRegistryKey){
            console.error('######## DOUBLE REGISTER OF', context._actionRegistryKey, context.action.key)
        }

        context._actionRegistryKey = internals.getKey()
        internals.actionRegistry[context._actionRegistryKey] = context

        return internals.actionRegistry[context._actionRegistryKey]
    },

    /**
     * removes a given action context from internal actionRegistryStorage.
     *
     * @param context
     * @returns void
     */
    deregister: function(context){
        if(internals.actionRegistry[(context || {})._actionRegistryKey || 'noKeyFound']){

            delete internals.actionRegistry[context._actionRegistryKey]

            // prevent _actionRegistryKey from being removed if the actionContext has been registered as a re-performer.
            if(!context._actionRegistryReperformerRegistered){
                delete context._actionRegistryKey
            }

            // resolve skip promise if actionRegistry got empty.
            if(this.getNrOfActions() < 1 && internals.skipDeferred){
                internals.skipDeferred.resolve()
                delete internals.skipDeferred
            }
            return
        }

        // debug information
        console.error('context not found in actionRegistry', context._actionRegistryKey, context.action.key, context.component.componentClass, `[${context.component.componentIndex}]`, context)
    },

    /**
     * @returns {Number} returns number of actions stored within the actionRegistry.
     */
    getNrOfActions: function(){
        return utils.collectionSize(internals.actionRegistry)
    },



    //////////////////////////////////
    ///  error handling
    //////////////////////////////////

    /**
     * returns true if error handling is skipped at the moment.
     *
     * @returns boolean
     */
    isFurtherErrorHandlingSkipped: function(){
        return internals.skipFurtherErrorHandlingViaComponentIndex !== false
    },

    /**
     * skips further error handling until the actual components action in progress is done.
     * this may not work if another component has skipped further error handling before this call.
     */
    skipFurtherErrorHandling: function(componentIndex){
        if(internals.skipFurtherErrorHandlingViaComponentIndex === false){
            internals.skipFurtherErrorHandlingViaComponentIndex = componentIndex
        }
    },

    /**
     * releases the skip status of the actual error handling if this is the component which has been skipped further error handling in the first place.
     */
    releaseFurtherErrorHandling: function(componentIndex){
        // only the component which has skipped all further error handling may un-skip it again.
        if(internals.skipFurtherErrorHandlingViaComponentIndex === componentIndex){
            if(this.getNrOfActions() < 1){
                internals.skipFurtherErrorHandlingViaComponentIndex = false
            } else {
                internals.skipDeferred = (utils.$ || window.$).Deferred();
                const skipPromise = internals.skipDeferred.promise();
                skipPromise.then(() => {
                    internals.skipFurtherErrorHandlingViaComponentIndex = false
                })
            }
        }
    },



    //////////////////////////////////
    ///  reperform registry
    //////////////////////////////////

    /**
     * registers a given actionContext within a group identified by the given reperformType which may be performed later.
     *
     * @param context           {Object} - actionContext
     * @param reperformType     {String} - re-perform type e.g., relogin
     */
    registerReperformer: function(context, reperformType = ''){
        if(!context){
            console.error('missing context')
            return
        }
        if(!context._actionRegistryKey){
            console.error('missing context._actionRegistryKey')
            return
        }
        if(!reperformType){
            console.error('missing reperformType')
            return
        }

        context._actionRegistryReperformerRegistered = true
        internals.reperformActionRegistry[reperformType] = internals.reperformActionRegistry[reperformType] || []
        internals.reperformActionRegistry[reperformType].push(context)
    },

    /**
     * reperforms all registered actions according to the given reperformType.
     *
     * @param reperformType     string - reperformType
     * @returns Promise
     */
    reperformRegisteredActions: function(reperformType = ''){
        if(!reperformType){
            return utils.promise.reject(new ClientError('MISSING_REPERFORM_TYPE'))
        }
        if(utils.isEmpty(internals.reperformActionRegistry[reperformType])){
            return utils.promise.resolve()
        }

        return utils.promise.resolveAll(
            this,
            internals.reperformActionRegistry[reperformType].map(context => (() => context.component.performAction(context.action)))
        )
            .then(status => {
                delete internals.reperformActionRegistry[reperformType]
                return true
            })
    },
}
